import { useEffect } from "react";
import {
  Button,
  DateInput,
  Form,
  SaveButton,
  SelectInput,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "react-admin";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Box, DialogActions, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { FormattedNumberInput } from "@/components/ui/react-admin/FormattedNumberInput";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import { ReportingCadenceValues, validateReportingCadenceFields } from "@/utils/validations/policyValidation";
import { Policy, ResourceName, type DateYMDString } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatDateDisplay, parseDateInput } from "./PolicyCreateInputs";
import { CADENCE_CHOICES, MONTH_DAY_CHOICES, WEEKDAY_CHOICES } from "./reportingCadenceConstants";

const getPolicyInputProps = (key: string) => getInputProps(key, {}, ResourceName.POLICIES);

/**
 * Builds the single PATCH payload for a reporting-cadence change. The opposite
 * day field is always cleared so the server-side validateReportingCadence (which
 * requires cadence + matching day together) accepts the change atomically — this
 * is why cadence cannot be set via per-field inline editing.
 */
export const buildReportingCadencePatch = (values: ReportingCadenceValues) => {
  const cadence = values.reporting_cadence || null;
  return {
    reporting_cadence: cadence,
    reporting_day_of_month: cadence === "monthly" ? (values.reporting_day_of_month ?? null) : null,
    reporting_day_of_week: cadence === "weekly" ? (values.reporting_day_of_week ?? null) : null,
    reporting_start_date: values.reporting_start_date ?? null,
    task_lead_days: values.task_lead_days ?? null,
  };
};

/** Dialog-level validation: a cadence must be chosen, then the shared field rules apply. */
const validate = (values: ReportingCadenceValues): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!values.reporting_cadence) {
    errors["reporting_cadence"] = "Select a cadence";
  }
  return validateReportingCadenceFields(values, errors);
};

const CadenceFields = ({ effectiveStartDate }: { effectiveStartDate?: DateYMDString | null }) => {
  const cadence = useWatch({ name: "reporting_cadence" }) as string | undefined;
  const { setValue, getValues } = useFormContext();

  // Default the reporting start date to the policy's effective start date once a
  // cadence is picked (mirrors the create dialog).
  useEffect(() => {
    if (!cadence || !effectiveStartDate) return;
    if (!getValues("reporting_start_date")) {
      setValue("reporting_start_date", effectiveStartDate, { shouldValidate: true });
    }
  }, [cadence, effectiveStartDate, getValues, setValue]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <SelectInput
        {...getPolicyInputProps("reporting_cadence")}
        choices={CADENCE_CHOICES}
        parse={(v) => (v === "" ? null : v)}
      />

      {cadence === "monthly" && (
        <SelectInput
          {...getPolicyInputProps("reporting_day_of_month")}
          choices={MONTH_DAY_CHOICES}
          parse={(v) => (v === "" || v === null || v === undefined ? null : Number(v))}
        />
      )}

      {cadence === "weekly" && (
        <SelectInput
          {...getPolicyInputProps("reporting_day_of_week")}
          choices={WEEKDAY_CHOICES}
          parse={(v) => (v === "" || v === null || v === undefined ? null : Number(v))}
        />
      )}

      <DateInput {...getPolicyInputProps("reporting_start_date")} parse={parseDateInput} format={formatDateDisplay} />

      <FormattedNumberInput {...getPolicyInputProps("task_lead_days")} displayFormat="number" />
    </Box>
  );
};

interface ReportingCadenceDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Set up or change a policy's reporting cadence from the detail page. Submits
 * cadence + day + start date + lead days in ONE PATCH so the server's atomic
 * cadence/day validation passes — the per-field inline editors cannot do this.
 */
const ReportingCadenceDialog = ({ open, onClose }: ReportingCadenceDialogProps) => {
  const record = useRecordContext<Policy>();
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  if (!record?.id) return null;

  const isEdit = !!record.reporting_cadence;

  const onSubmit = async (values: ReportingCadenceValues) => {
    // The policy data provider uses PUT (full replace) — send the whole record
    // merged with the cadence change, the same way the inline editors do, or the
    // backend overwrites the omitted fields. returnPromise so a server-side
    // validation error rejects here and the dialog stays open.
    try {
      await update(
        ResourceName.POLICIES,
        { id: record.id, data: { ...record, ...buildReportingCadencePatch(values) }, previousData: record },
        { returnPromise: true }
      );
      notify(isEdit ? "Reporting cadence updated" : "Reporting cadence set up", { type: "success" });
      refresh();
      onClose();
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Change Reporting Cadence" : "Set Up Reporting Cadence"}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 16, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Form
        onSubmit={(values: FieldValues) => void onSubmit(values as ReportingCadenceValues)}
        validate={(values: FieldValues) => validate(values as ReportingCadenceValues)}
        defaultValues={{
          reporting_cadence: record.reporting_cadence ?? null,
          reporting_day_of_month: record.reporting_day_of_month ?? null,
          reporting_day_of_week: record.reporting_day_of_week ?? null,
          reporting_start_date: record.reporting_start_date ?? null,
          task_lead_days: record.task_lead_days ?? 5,
        }}
      >
        <DialogContent>
          <CadenceFields effectiveStartDate={record.effective_start_date} />
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={onClose} variant="outlined" />
          {/* alwaysEnable: the "Change cadence" form opens pre-filled (pristine), so the
              default dirty-gating would leave Save disabled. */}
          <SaveButton size="medium" type="submit" alwaysEnable disabled={isPending} />
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default ReportingCadenceDialog;
